import el from "@cypherpotato/el";
import { api } from "../../api";
import { Card, CardContent, CardGrid, CardHeader } from "../../components/card";
import { Dashboard, DashboardTitle } from "../../components/dashboard";
import { Editor, EditorDescription, EditorRow, EditorTitle } from "../../components/editor";
import { Content, Section } from "../../components/layout";
import { Meter } from "../../components/meter";
import { Heading, MutedText, Paragraph } from "../../components/text";
import { formatCurrency, formatDateTime, formatFloat, formatNumber } from "../../ui/format";
import { runOperation } from "../../ui/operation";
import { DisplayCreditAdditionModal } from "../../helpers/invoice-helpers";
import { ButtonLink } from "../../components/link";
import { DefaultRow, Table, TableBody, TableCell, TableHeader, TableHeaderCell, TableRow } from "../../components/table";

export default function () {
    const usage = el.state.create(null);

    function formatBytes(value) {
        if (value >= 1024 ** 3)
            return `${formatFloat(value / 1024 ** 3)} GB`;

        if (value >= 1024 ** 2)
            return `${formatFloat(value / 1024 ** 2)} MB`;

        if (value >= 1024)
            return `${formatFloat(value / 1024)} KB`;

        return `${formatNumber(value)} B`;
    }

    runOperation("Loading usage data...", async () => {
        const result = await api.getJson("/api/v1/information/balance");
        usage.value = result.data;
    });

    return Dashboard(
        DashboardTitle(
            "Usage",
            "Track your current plan limits and included resources."
        ),
        el.state.listening([usage], () => {
            const data = usage.value;
            if (!data)
                return Section(MutedText("Loading usage data..."));

            const meterProps = {
                min: 0,
                max: 100,
                colorShade: true,
                props: {
                    classList: ["full-width"]
                }
            };
            const storagePercent = data.planLimits?.includedStorage > 0
                ? Math.min((data.storageUsage || 0) / data.planLimits.includedStorage * 100, 100)
                : 0;
            const subscriptionConsumptions = data.subscriptionModelUsage?.consumptions || [];
            const hasSubscriptionUsage = subscriptionConsumptions.length > 0;

            return Content(
                Section(
                    CardGrid(
                        Card(
                            CardHeader("Current plan"),
                            CardContent(el("h2", { style: { margin: 0, fontWeight: 400 } }, data.plan))
                        )
                    )
                ),
                Heading("Balance"),
                Section(
                    Editor(
                        {
                            readonly: true,
                            hoverState: false,
                            renderValue() {
                                return el("h3", formatCurrency(data.balance))
                            }
                        },
                        EditorDescription(
                            EditorTitle("Account balance"),
                            Paragraph("Account balance is used for inference, RAG, subscriptions and AIVAX services.")
                        )
                    ),
                    EditorRow(
                        ButtonLink("Top up account", {
                            onClick() {
                                DisplayCreditAdditionModal();
                            }
                        })
                    )
                ),
                Heading("Storage"),
                Section(
                    Editor(
                        {
                            readonly: true,
                            hoverState: false,
                            renderValue() {
                                return Meter({ value: storagePercent, ...meterProps });
                            }
                        },
                        EditorDescription(
                            EditorTitle("Storage usage"),
                            Paragraph(`${formatBytes(data.storageUsage || 0)} / ${formatBytes(data.planLimits?.includedStorage || 0)} used`)
                        ),
                    ),

                ),
                hasSubscriptionUsage && Heading("Subscription usage"),
                hasSubscriptionUsage && Section(
                    ...subscriptionConsumptions.map(consumption =>
                        Editor(
                            {
                                readonly: true,
                                hoverState: false,
                                renderValue() {
                                    return Meter({ value: Math.min(consumption.usedPercent, 100), ...meterProps });
                                }
                            },
                            EditorDescription(
                                EditorTitle(consumption.name),
                                Paragraph(
                                    `${formatFloat(consumption.usedPercent)}% used. `,
                                    consumption.resetAt ? `Resets at ${formatDateTime(consumption.resetAt)}.` : "No active consumption window."
                                )
                            )
                        )
                    )
                ),
                hasSubscriptionUsage && Section(
                    Heading("Subscription usage breakdown"),
                    CardGrid(
                        ...subscriptionConsumptions.map(consumption =>
                            Card({ classList: ["bg-card"] },
                                CardHeader(consumption.name),
                                CardContent(
                                    Table(
                                        TableHeader(
                                            TableHeaderCell("Usage"),
                                            TableHeaderCell("Share")
                                        ),
                                        TableBody(
                                            consumption.details.map(detail =>
                                                TableRow(
                                                    TableCell(detail.usageName),
                                                    TableCell(formatFloat(detail.usageSharePercent), "%")
                                                )
                                            )?.nullWhenEmpty() || DefaultRow("No usage in this period.")
                                        )
                                    )
                                )
                            )
                        )
                    )
                )
            );
        })
    );
}
